import { useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { MessagesSquare, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmModal, InlineEdit } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useConversations,
  useDeleteConversation,
  useUpdateConversation,
  useTogglePinConversation,
} from '../../hooks/useConversations';
import { groupByDay } from '@/lib/conversation-groups';
import { routes } from '@/lib/routes';
import { useSidebarCollapse } from '../layout/sidebar-collapse-context';
import { ConversationItemMenu } from './ConversationItemMenu';
import type { Conversation } from '../../types';
import { cleanDisplayText } from '@/utils/display-text';

interface ChatNavItemProps {
  collapsed: boolean;
}

const PINNED_CAP = 5;
const TODAY_CAP = 8;
const YESTERDAY_CAP = 5;
const EARLIER_CAP = 5;

export function ChatNavItem({ collapsed }: ChatNavItemProps) {
  const navigate = useNavigate();
  const { data } = useConversations();
  const { setCollapsed } = useSidebarCollapse();
  const { pathname } = useLocation();
  const chatMatch = useMatch('/chat/:conversationId');
  const onChatDetail = !!chatMatch;
  const inChatArea =
    pathname === '/chat' || pathname.startsWith('/chat/');
  const expandedViaChatRef = useRef(false);
  const activeConversationId = chatMatch?.params.conversationId ?? null;

  const updateConversation = useUpdateConversation();
  const deleteConversation = useDeleteConversation();
  const togglePinConversation = useTogglePinConversation();
  const { success, error: showError } = useToast();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversation: Conversation | null;
  }>({ isOpen: false, conversation: null });

  const conversations = useMemo(() => data?.items ?? [], [data?.items]);
  // Complete, unpaginated pinned set (first page only); excluded from `items`.
  const pinned = useMemo(() => data?.pinned ?? [], [data?.pinned]);

  const mostRecentId = useMemo(() => {
    let best: Conversation | null = null;
    for (const c of [...pinned, ...conversations]) {
      if (!best || new Date(c.updatedAt) > new Date(best.updatedAt)) {
        best = c;
      }
    }
    return best?.conversationId ?? null;
  }, [pinned, conversations]);

  const groups = useMemo(() => groupByDay(conversations), [conversations]);

  const handleNewChat = () => {
    if (expandedViaChatRef.current) {
      expandedViaChatRef.current = false;
      setCollapsed(true);
    }
    navigate(routes.operator.chatNew);
  };

  const handleConversationSelect = () => {
    if (expandedViaChatRef.current) {
      expandedViaChatRef.current = false;
      setCollapsed(true);
    }
  };

  const handleChatRowClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If the sidebar was opened via the Chat icon, clicking Chat again
    // just collapses it back — don't navigate to the chat list page.
    if (expandedViaChatRef.current) {
      e.preventDefault();
      expandedViaChatRef.current = false;
      setCollapsed(true);
    }
  };

  const handleRename = async (conversationId: string, name: string) => {
    try {
      await updateConversation.mutateAsync({
        conversationId,
        input: { name: name || undefined },
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to rename conversation');
    }
  };

  const handleTogglePin = (conversation: Conversation) => {
    togglePinConversation.mutate(
      { conversationId: conversation.conversationId, pinned: !conversation.pinned },
      {
        onError: (err) =>
          showError(err instanceof Error ? err.message : 'Failed to update pin'),
      },
    );
  };

  const handleDeleteConfirm = () => {
    const target = deleteModal.conversation;
    if (!target) return;
    setDeleteModal({ isOpen: false, conversation: null });
    if (activeConversationId === target.conversationId) {
      navigate(routes.operator.chats);
    }
    deleteConversation.mutate(target.conversationId, {
      onSuccess: () => success('Conversation deleted'),
      onError: (err) =>
        showError(err instanceof Error ? err.message : 'Failed to delete conversation'),
    });
  };

  if (collapsed) {
    // `to` is the list page; `end={false}` makes NavLink set aria-current=page
    // for /chat AND any /chat/:id, so the icon highlights any time we're
    // in the chat area.
    const handleCollapsedClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onChatDetail) {
        // Already viewing a chat — expand the sidebar instead of navigating.
        e.preventDefault();
        expandedViaChatRef.current = true;
        setCollapsed(false);
        return;
      }
      // Otherwise jump straight into the most-recent conversation (if any).
      if (mostRecentId) {
        e.preventDefault();
        navigate(routes.operator.chat(mostRecentId));
      }
    };
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <NavLink
            to={routes.operator.chats}
            end={false}
            onClick={handleCollapsedClick}
            className={cn(
              'group flex items-center justify-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              'aria-[current=page]:text-primary',
            )}
          >
            <MessagesSquare className="h-5 w-5 shrink-0" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>Chat</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const sectionProps = {
    activeConversationId,
    editingId,
    onSelect: handleConversationSelect,
    onStartRename: (id: string) => setEditingId(id),
    onStopRename: () => setEditingId(null),
    onRename: handleRename,
    onTogglePin: handleTogglePin,
    onRequestDelete: (c: Conversation) =>
      setDeleteModal({ isOpen: true, conversation: c }),
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <NavLink
          to={routes.operator.chats}
          end
          onClick={handleChatRowClick}
          className={({ isActive }) =>
            cn(
              'group flex flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive || inChatArea
                ? 'text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            )
          }
        >
          <MessagesSquare className="h-5 w-5 shrink-0" />
          <span className="flex-1">Chat</span>
        </NavLink>
        <button
          type="button"
          onClick={handleNewChat}
          aria-label="New Chat"
          className="focus-ring flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          title="New Chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {(conversations.length > 0 || pinned.length > 0) && (
        <div className="space-y-2 px-1 pb-1">
          {pinned.length > 0 && (
            <ConversationSection
              title="Pinned"
              items={pinned.slice(0, PINNED_CAP)}
              {...sectionProps}
            />
          )}
          {groups.today.length > 0 && (
            <ConversationSection
              title="Today"
              items={groups.today.slice(0, TODAY_CAP)}
              {...sectionProps}
            />
          )}
          {groups.yesterday.length > 0 && (
            <ConversationSection
              title="Yesterday"
              items={groups.yesterday.slice(0, YESTERDAY_CAP)}
              {...sectionProps}
            />
          )}
          {groups.earlier.length > 0 && (
            <ConversationSection
              title="Earlier"
              items={groups.earlier.slice(0, EARLIER_CAP)}
              {...sectionProps}
            />
          )}
          <NavLink
            to={routes.operator.chats}
            end
            className="focus-ring block px-3 pt-1 text-[11px] text-muted-foreground/60 hover:text-muted-foreground"
          >
            See all
          </NavLink>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversation: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Chat"
        message={`Are you sure you want to delete "${
          cleanDisplayText(deleteModal.conversation?.name) || 'New Conversation'
        }"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteConversation.isPending}
      />
    </div>
  );
}

interface ConversationSectionProps {
  title: string;
  items: Conversation[];
  activeConversationId: string | null;
  editingId: string | null;
  onSelect: () => void;
  onStartRename: (conversationId: string) => void;
  onStopRename: () => void;
  onRename: (conversationId: string, name: string) => Promise<void>;
  onTogglePin: (conversation: Conversation) => void;
  onRequestDelete: (conversation: Conversation) => void;
}

function ConversationSection({
  title,
  items,
  activeConversationId,
  editingId,
  onSelect,
  onStartRename,
  onStopRename,
  onRename,
  onTogglePin,
  onRequestDelete,
}: ConversationSectionProps) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-2 px-3 pb-1 pt-1">
        <span className="text-[10px] text-muted-foreground/60">
          {title}
        </span>
        <span className="h-px flex-1 bg-border/50" />
      </div>
      {items.map((c) => (
        <ChatConversationRow
          key={c.conversationId}
          conversation={c}
          isActive={activeConversationId === c.conversationId}
          isEditing={editingId === c.conversationId}
          onSelect={onSelect}
          onStartRename={() => onStartRename(c.conversationId)}
          onStopRename={onStopRename}
          onRename={(name) => onRename(c.conversationId, name)}
          onTogglePin={() => onTogglePin(c)}
          onRequestDelete={() => onRequestDelete(c)}
        />
      ))}
    </div>
  );
}

interface ChatConversationRowProps {
  conversation: Conversation;
  isActive: boolean;
  isEditing: boolean;
  onSelect: () => void;
  onStartRename: () => void;
  onStopRename: () => void;
  onRename: (name: string) => Promise<void>;
  onTogglePin: () => void;
  onRequestDelete: () => void;
}

function ChatConversationRow({
  conversation,
  isActive,
  isEditing,
  onSelect,
  onStartRename,
  onStopRename,
  onRename,
  onTogglePin,
  onRequestDelete,
}: ChatConversationRowProps) {
  const navigate = useNavigate();

  const go = () => {
    if (isEditing) return;
    onSelect();
    navigate(routes.operator.chat(conversation.conversationId));
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isEditing) {
          e.preventDefault();
          go();
        }
      }}
      className={cn(
        'focus-ring group flex items-center gap-1 rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer',
        isActive
          ? 'text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      )}
    >
      <span className="min-w-0 flex-1 truncate">
        <InlineEdit
          value={conversation.name || ''}
          placeholder="New Conversation"
          externalEditing={isEditing}
          disableClickToEdit
          hideHoverHint
          onEditingChange={(editing) => {
            if (!editing) onStopRename();
          }}
          onSave={onRename}
        />
      </span>
      <ConversationItemMenu
        isPinned={!!conversation.pinned}
        onRename={onStartRename}
        onTogglePin={onTogglePin}
        onDelete={onRequestDelete}
        className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
      />
    </div>
  );
}
